import { SEED_UNITS, SEED_PROFESSIONALS, SEED_USERS, SEED_PERFORMANCE, SEED_SERVICES, SEED_PACKAGES, SEED_UNIT_PERFORMANCE, SEED_HOLIDAYS, SEED_UNIT_CONFIG, SEED_UNIT_METAS, SEED_DAILY_REVENUE } from '../data/seedData.js';
import { reprojetarLocal } from './cadastroSensivel.js';
import { cloudSave, cloudLoad, cloudLoadAll, cloudSaveAll, CLOUD_DATA_KEYS, CLOUD_NOT_MODIFIED, CLOUD_UNAVAILABLE, isDirty, getDirtyKeys, flushPendingUploads, confirmCloudMeta, esquecerCloudMeta, envioRecusado } from './supabase.js';
import { SEED_TRAVADO, PERF_MERGE_POR_UNIDADE, CADASTRO_SEED_TRAVADO, MERGE_JANELA_SO_LOCAL } from '../config/featureFlags.js';
import { ehCarimboSeed } from './seedCarimbo.js';

export { flushPendingUploads };

const KEYS = {
  USERS: 'fk_users',
  UNITS: 'fk_units',
  PROFESSIONALS: 'fk_professionals',
  PERFORMANCE: 'fk_performance',
  SERVICES: 'fk_services',
  PACKAGES: 'fk_packages',
  UNIT_PERFORMANCE: 'fk_unit_performance',
  DAILY_REVENUE: 'fk_daily_revenue',
  UNIT_METAS: 'fk_unit_metas',
  PROF_CHALLENGES: 'fk_prof_challenges',
  HOLIDAYS: 'fk_holidays',
  UNIT_CONFIG: 'fk_unit_config',
  INITIALIZED: 'fk_initialized',
  SCHEMA_VERSION: 'fk_schema_version',
  WEEKLY_DUELS: 'fk_weekly_duels',
};

const CURRENT_SCHEMA_VERSION = '32';

// CADASTRO_SEED_TRAVADO (28/10/2026, caso Renato): o cadastro (profissionais,
// unidades, usuários) e os dias fechados nunca mais nascem do seed neste
// aparelho; sem cópia, a lista fica vazia até o cofre responder. E a chave do
// cofre que o initStorage mexeu, ou que não tem cópia no aparelho (ou todas, se
// o aparelho foi apagado: sem fk_initialized) esquece o ETag: a primeira leitura
// vem cheia, nunca "não mudou" por cima de uma cópia que o cofre não mandou.
const SEM_SEED = CADASTRO_SEED_TRAVADO;
const lerBruto = (k) => { try { return localStorage.getItem(k); } catch { return null; } };

// Returns true if a schema migration ran (meaning local data is newer than cloud)
export function initStorage() {
  const apagado = !lerBruto(KEYS.INITIALIZED);
  const antes = SEM_SEED ? new Map(CLOUD_DATA_KEYS.map(k => [k, lerBruto(k)])) : null;
  const migrated = initStorageSemEtag();
  if (SEM_SEED) {
    for (const k of CLOUD_DATA_KEYS) {
      const agora = lerBruto(k);
      if (apagado || agora === null || agora !== antes.get(k)) esquecerCloudMeta(k);
    }
  }
  relerChavesDoMergeUmaVez();
  return migrated;
}

// MERGE_JANELA_SO_LOCAL (29/10/2026, Meta de Moema): o aparelho que ficou preso
// numa cópia que o merge manteve por cima do cofre tem o ETag da versão atual e
// recebe "não mudou" para sempre. Uma vez por aparelho, as chaves do merge
// esquecem o ETag e a próxima leitura vem cheia (com a regra nova, o cofre vence).
const MARCA_RELIDO_MERGE = 'fk_merge_janela_relido_20261029';
function relerChavesDoMergeUmaVez() {
  if (!MERGE_JANELA_SO_LOCAL || lerBruto(MARCA_RELIDO_MERGE)) return;
  for (const k of MERGE_BY_UPDATED_AT_KEYS) esquecerCloudMeta(k);
  try { localStorage.setItem(MARCA_RELIDO_MERGE, new Date().toISOString()); } catch { /* sem armazenamento: relê de novo no próximo boot */ }
}

function initStorageSemEtag() {
  let migrated = false;
  if (!localStorage.getItem(KEYS.INITIALIZED)) {
    if (!SEM_SEED) {
      localStorage.setItem(KEYS.USERS, JSON.stringify(SEED_USERS));
      localStorage.setItem(KEYS.UNITS, JSON.stringify(SEED_UNITS));
      localStorage.setItem(KEYS.PROFESSIONALS, JSON.stringify(SEED_PROFESSIONALS));
    }
    localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
    localStorage.setItem(KEYS.INITIALIZED, 'true');
  }
  // Always init services/packages if missing (handles existing installs)
  if (!localStorage.getItem(KEYS.SERVICES)) {
    localStorage.setItem(KEYS.SERVICES, JSON.stringify(SEED_SERVICES));
  }
  if (!localStorage.getItem(KEYS.PACKAGES)) {
    localStorage.setItem(KEYS.PACKAGES, JSON.stringify(SEED_PACKAGES));
  }
  if (!localStorage.getItem(KEYS.UNIT_PERFORMANCE)) {
    localStorage.setItem(KEYS.UNIT_PERFORMANCE, JSON.stringify(SEED_UNIT_PERFORMANCE));
  }
  // SEED_TRAVADO (23/10/2026): o Meta x Realizado (faturamento diário e metas)
  // nunca mais nasce de seed; sem a chave, a tela lê [] até o cofre responder.
  if (!SEED_TRAVADO && !localStorage.getItem(KEYS.DAILY_REVENUE)) {
    localStorage.setItem(KEYS.DAILY_REVENUE, JSON.stringify(SEED_DAILY_REVENUE));
  }
  if (!SEED_TRAVADO && !localStorage.getItem(KEYS.UNIT_METAS)) {
    localStorage.setItem(KEYS.UNIT_METAS, JSON.stringify(SEED_UNIT_METAS));
  }
  if (!SEM_SEED && !localStorage.getItem(KEYS.HOLIDAYS)) {
    localStorage.setItem(KEYS.HOLIDAYS, JSON.stringify(SEED_HOLIDAYS));
  }
  if (!localStorage.getItem(KEYS.UNIT_CONFIG)) {
    localStorage.setItem(KEYS.UNIT_CONFIG, JSON.stringify(SEED_UNIT_CONFIG));
  }
  // Migrate when schema is outdated
  if (localStorage.getItem(KEYS.SCHEMA_VERSION) !== CURRENT_SCHEMA_VERSION) {
    migrated = true;
    // Migrate service categories
    const seedMap = Object.fromEntries(SEED_SERVICES.map(s => [s.id, s.category]));
    try {
      const existing = JSON.parse(localStorage.getItem(KEYS.SERVICES)) || [];
      const migrated = existing.map(s => seedMap[s.id] ? { ...s, category: seedMap[s.id] } : s);
      localStorage.setItem(KEYS.SERVICES, JSON.stringify(migrated));
    } catch {}
    // Merge new professionals + fix Day's role (CADASTRO_SEED_TRAVADO: não roda;
    // era aqui que o seed voltava num aparelho com a cópia do cofre)
    if (!SEM_SEED) try {
      const existingPros = JSON.parse(localStorage.getItem(KEYS.PROFESSIONALS)) || [];
      const existingIds = new Set(existingPros.map(p => p.id));
      const updated = existingPros.map(p =>
        p.id === 'p-vila-day' && p.role === 'Profissional' ? { ...p, role: 'Esteticista' } : p
      );
      const newPros = SEED_PROFESSIONALS.filter(p => !existingIds.has(p.id));
      localStorage.setItem(KEYS.PROFESSIONALS, JSON.stringify([...updated, ...newPros]));
    } catch {}
    // Merge new users (CADASTRO_SEED_TRAVADO: não roda)
    if (!SEM_SEED) try {
      const existingUsers = JSON.parse(localStorage.getItem(KEYS.USERS)) || [];
      const existingUserIds = new Set(existingUsers.map(u => u.id));
      const newUsers = SEED_USERS.filter(u => !existingUserIds.has(u.id));
      if (newUsers.length > 0) {
        localStorage.setItem(KEYS.USERS, JSON.stringify([...existingUsers, ...newUsers]));
      }
    } catch {}
    // Merge new performance records + add pacotesVendidos to existing
    try {
      const existingPerf = JSON.parse(localStorage.getItem(KEYS.PERFORMANCE)) || [];
      const existingPerfIds = new Set(existingPerf.map(r => r.id));
      const migratedPerf = existingPerf.map(r => {
        if (!r.pacotesVendidos) {
          const seed = SEED_PERFORMANCE.find(s => s.id === r.id);
          return { ...r, pacotesVendidos: seed?.pacotesVendidos || { 'pkg-basic': 0, 'pkg-premium': 0, 'pkg-vip': 0 } };
        }
        return r;
      });
      const newPerf = SEED_PERFORMANCE.filter(r => !existingPerfIds.has(r.id));
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify([...migratedPerf, ...newPerf]));
    } catch {}
    // Fix corrupted service categories: 'Barba' -> 'Barbeiro'
    try {
      const svcs = JSON.parse(localStorage.getItem(KEYS.SERVICES)) || [];
      const fixed = svcs.map(s => s.category === 'Barba' ? { ...s, category: 'Barbeiro' } : s);
      localStorage.setItem(KEYS.SERVICES, JSON.stringify(fixed));
    } catch {}
    // Merge unit performance seed data
    try {
      const existingUP = JSON.parse(localStorage.getItem(KEYS.UNIT_PERFORMANCE)) || [];
      const existingUPIds = new Set(existingUP.map(r => r.id));
      const newUP = SEED_UNIT_PERFORMANCE.filter(r => !existingUPIds.has(r.id));
      if (newUP.length > 0) {
        localStorage.setItem(KEYS.UNIT_PERFORMANCE, JSON.stringify([...existingUP, ...newUP]));
      }
    } catch {}
    // v5: Seed Meta x Realizado data + replace unit-alpha with unit-jardins
    try {
      // Replace unit-alpha with unit-jardins in all stored data
      const replaceAlpha = (arr) => arr.map(item => {
        const json = JSON.stringify(item);
        if (json.includes('unit-alpha') || json.includes('p-alpha-') || json.includes('.alpha@')) {
          return JSON.parse(json.replace(/unit-alpha/g, 'unit-jardins').replace(/p-alpha-/g, 'p-jardins-').replace(/\.alpha@/g, '.jardins@').replace(/Alphaville/g, 'Jardins').replace(/Barueri/g, 'São Paulo').replace(/user-lobao-alpha/g, 'user-lobao-jardins').replace(/user-pedro-alpha/g, 'user-pedro-jardins').replace(/user-damas-alpha/g, 'user-damas-jardins').replace(/user-bianca-alpha/g, 'user-bianca-jardins').replace(/user-thiago-alpha/g, 'user-thiago-jardins'));
        }
        return item;
      });
      [KEYS.UNITS, KEYS.PROFESSIONALS, KEYS.USERS, KEYS.PERFORMANCE, KEYS.UNIT_PERFORMANCE].forEach(key => {
        try {
          // CADASTRO_SEED_TRAVADO: chave que não existe continua sem existir (antes virava [])
          if (SEM_SEED && localStorage.getItem(key) === null) return;
          const data = JSON.parse(localStorage.getItem(key)) || [];
          localStorage.setItem(key, JSON.stringify(replaceAlpha(data)));
        } catch {}
      });
    } catch {}
    // Seed new Meta x Realizado keys if missing (SEED_TRAVADO: faturamento
    // diário e metas nunca mais nascem de seed, nem por aqui)
    try {
      if (!SEED_TRAVADO && (!localStorage.getItem(KEYS.DAILY_REVENUE) || JSON.parse(localStorage.getItem(KEYS.DAILY_REVENUE)).length === 0)) {
        localStorage.setItem(KEYS.DAILY_REVENUE, JSON.stringify(SEED_DAILY_REVENUE));
      }
      if (!SEED_TRAVADO && (!localStorage.getItem(KEYS.UNIT_METAS) || JSON.parse(localStorage.getItem(KEYS.UNIT_METAS)).length === 0)) {
        localStorage.setItem(KEYS.UNIT_METAS, JSON.stringify(SEED_UNIT_METAS));
      }
      if (!SEM_SEED && (!localStorage.getItem(KEYS.HOLIDAYS) || JSON.parse(localStorage.getItem(KEYS.HOLIDAYS)).length === 0)) {
        localStorage.setItem(KEYS.HOLIDAYS, JSON.stringify(SEED_HOLIDAYS));
      }
      if (!localStorage.getItem(KEYS.UNIT_CONFIG) || JSON.parse(localStorage.getItem(KEYS.UNIT_CONFIG)).length === 0) {
        localStorage.setItem(KEYS.UNIT_CONFIG, JSON.stringify(SEED_UNIT_CONFIG));
      }
    } catch {}
    // v6: Update service categories (Barbearia->proper role categories), add new services, add faturamentoProdutos
    try {
      // Replace all services with updated seed data (new categories + new services)
      localStorage.setItem(KEYS.SERVICES, JSON.stringify(SEED_SERVICES));
      // Add faturamentoProdutos to existing unit performance records
      const existingUP = JSON.parse(localStorage.getItem(KEYS.UNIT_PERFORMANCE)) || [];
      const migratedUP = existingUP.map(r => {
        if (r.faturamentoProdutos === undefined) {
          return { ...r, faturamentoProdutos: 0 };
        }
        return r;
      });
      localStorage.setItem(KEYS.UNIT_PERFORMANCE, JSON.stringify(migratedUP));
    } catch {}
    // v8: Replace unit performance with COMPLETE real data from Avec SalãoVIP (all 5 units)
    try {
      const AVEC_REAL_DATA = {
        'unit-vila': {
          '2025-4': { faturamento: 131152, servicosRealizados: 1068, clientesAtendidos: 696, clientesNovos: 181, faturamentoProdutos: 6789, produtosVendidos: 68 },
          '2025-5': { faturamento: 103878, servicosRealizados: 1067, clientesAtendidos: 663, clientesNovos: 148, faturamentoProdutos: 4180, produtosVendidos: 44 },
          '2025-6': { faturamento: 110894, servicosRealizados: 1103, clientesAtendidos: 642, clientesNovos: 175, faturamentoProdutos: 3414, produtosVendidos: 36 },
          '2025-7': { faturamento: 106080, servicosRealizados: 945, clientesAtendidos: 569, clientesNovos: 133, faturamentoProdutos: 8308, produtosVendidos: 81 },
          '2025-8': { faturamento: 109744, servicosRealizados: 1026, clientesAtendidos: 646, clientesNovos: 176, faturamentoProdutos: 4579, produtosVendidos: 44 },
          '2025-9': { faturamento: 120890, servicosRealizados: 1101, clientesAtendidos: 697, clientesNovos: 177, faturamentoProdutos: 6123, produtosVendidos: 52 },
          '2025-10': { faturamento: 119476, servicosRealizados: 1039, clientesAtendidos: 717, clientesNovos: 153, faturamentoProdutos: 4982, produtosVendidos: 43 },
          '2025-11': { faturamento: 115325, servicosRealizados: 1088, clientesAtendidos: 638, clientesNovos: 135, faturamentoProdutos: 6645, produtosVendidos: 61 },
          '2025-12': { faturamento: 110286, servicosRealizados: 1015, clientesAtendidos: 611, clientesNovos: 121, faturamentoProdutos: 3803, produtosVendidos: 34 },
          '2026-1': { faturamento: 97734, servicosRealizados: 826, clientesAtendidos: 515, clientesNovos: 118, faturamentoProdutos: 4101, produtosVendidos: 37 },
          '2026-2': { faturamento: 129050, servicosRealizados: 1033, clientesAtendidos: 608, clientesNovos: 128, faturamentoProdutos: 6532, produtosVendidos: 57 },
          '2026-3': { faturamento: 53535, servicosRealizados: 468, clientesAtendidos: 291, clientesNovos: 56, faturamentoProdutos: 1968, produtosVendidos: 16 },
        },
        'unit-itaim': {
          '2025-4': { faturamento: 59387, servicosRealizados: 681, clientesAtendidos: 443, clientesNovos: 137, faturamentoProdutos: 2496, produtosVendidos: 118 },
          '2025-5': { faturamento: 71332, servicosRealizados: 713, clientesAtendidos: 446, clientesNovos: 103, faturamentoProdutos: 4952, produtosVendidos: 209 },
          '2025-6': { faturamento: 60668, servicosRealizados: 624, clientesAtendidos: 404, clientesNovos: 102, faturamentoProdutos: 2675, produtosVendidos: 189 },
          '2025-7': { faturamento: 59926, servicosRealizados: 662, clientesAtendidos: 378, clientesNovos: 100, faturamentoProdutos: 2089, produtosVendidos: 156 },
          '2025-8': { faturamento: 71414, servicosRealizados: 671, clientesAtendidos: 407, clientesNovos: 102, faturamentoProdutos: 5174, produtosVendidos: 228 },
          '2025-9': { faturamento: 71133, servicosRealizados: 645, clientesAtendidos: 392, clientesNovos: 88, faturamentoProdutos: 6388, produtosVendidos: 182 },
          '2025-10': { faturamento: 64924, servicosRealizados: 673, clientesAtendidos: 403, clientesNovos: 91, faturamentoProdutos: 2969, produtosVendidos: 165 },
          '2025-11': { faturamento: 76452, servicosRealizados: 527, clientesAtendidos: 311, clientesNovos: 66, faturamentoProdutos: 3776, produtosVendidos: 104 },
          '2025-12': { faturamento: 61885, servicosRealizados: 648, clientesAtendidos: 395, clientesNovos: 105, faturamentoProdutos: 2401, produtosVendidos: 34 },
          '2026-1': { faturamento: 52910, servicosRealizados: 563, clientesAtendidos: 337, clientesNovos: 60, faturamentoProdutos: 1997, produtosVendidos: 24 },
          '2026-2': { faturamento: 71096, servicosRealizados: 504, clientesAtendidos: 331, clientesNovos: 100, faturamentoProdutos: 2188, produtosVendidos: 49 },
          '2026-3': { faturamento: 36713, servicosRealizados: 257, clientesAtendidos: 157, clientesNovos: 49, faturamentoProdutos: 2049, produtosVendidos: 24 },
        },
        'unit-moema': {
          '2025-4': { faturamento: 166902, servicosRealizados: 1225, clientesAtendidos: 695, clientesNovos: 156, faturamentoProdutos: 10749, produtosVendidos: 117 },
          '2025-5': { faturamento: 176993, servicosRealizados: 1211, clientesAtendidos: 702, clientesNovos: 139, faturamentoProdutos: 11968, produtosVendidos: 124 },
          '2025-6': { faturamento: 148179, servicosRealizados: 1050, clientesAtendidos: 641, clientesNovos: 126, faturamentoProdutos: 11614, produtosVendidos: 121 },
          '2025-7': { faturamento: 144924, servicosRealizados: 1094, clientesAtendidos: 687, clientesNovos: 111, faturamentoProdutos: 8739, produtosVendidos: 90 },
          '2025-8': { faturamento: 131648, servicosRealizados: 1141, clientesAtendidos: 722, clientesNovos: 93, faturamentoProdutos: 6148, produtosVendidos: 71 },
          '2025-9': { faturamento: 136411, servicosRealizados: 1114, clientesAtendidos: 665, clientesNovos: 104, faturamentoProdutos: 9042, produtosVendidos: 79 },
          '2025-10': { faturamento: 153509, servicosRealizados: 1101, clientesAtendidos: 711, clientesNovos: 87, faturamentoProdutos: 9851, produtosVendidos: 87 },
          '2025-11': { faturamento: 131542, servicosRealizados: 1159, clientesAtendidos: 645, clientesNovos: 98, faturamentoProdutos: 7137, produtosVendidos: 64 },
          '2025-12': { faturamento: 153309, servicosRealizados: 1342, clientesAtendidos: 735, clientesNovos: 89, faturamentoProdutos: 8875, produtosVendidos: 82 },
          '2026-1': { faturamento: 123990, servicosRealizados: 1002, clientesAtendidos: 598, clientesNovos: 106, faturamentoProdutos: 4605, produtosVendidos: 47 },
          '2026-2': { faturamento: 135935, servicosRealizados: 1032, clientesAtendidos: 591, clientesNovos: 76, faturamentoProdutos: 7220, produtosVendidos: 70 },
          '2026-3': { faturamento: 56932, servicosRealizados: 406, clientesAtendidos: 235, clientesNovos: 38, faturamentoProdutos: 2236, produtosVendidos: 19 },
        },
        'unit-oscar': {
          '2025-4': { faturamento: 62468, servicosRealizados: 543, clientesAtendidos: 272, clientesNovos: 141, faturamentoProdutos: 2640, produtosVendidos: 74 },
          '2025-5': { faturamento: 65410, servicosRealizados: 583, clientesAtendidos: 318, clientesNovos: 127, faturamentoProdutos: 4077, produtosVendidos: 54 },
          '2025-6': { faturamento: 63547, servicosRealizados: 530, clientesAtendidos: 302, clientesNovos: 94, faturamentoProdutos: 3741, produtosVendidos: 42 },
          '2025-7': { faturamento: 53554, servicosRealizados: 548, clientesAtendidos: 324, clientesNovos: 97, faturamentoProdutos: 3789, produtosVendidos: 40 },
          '2025-8': { faturamento: 74820, servicosRealizados: 663, clientesAtendidos: 348, clientesNovos: 134, faturamentoProdutos: 4460, produtosVendidos: 45 },
          '2025-9': { faturamento: 66305, servicosRealizados: 589, clientesAtendidos: 343, clientesNovos: 98, faturamentoProdutos: 4635, produtosVendidos: 45 },
          '2025-10': { faturamento: 77067, servicosRealizados: 663, clientesAtendidos: 364, clientesNovos: 103, faturamentoProdutos: 4152, produtosVendidos: 34 },
          '2025-11': { faturamento: 86093, servicosRealizados: 710, clientesAtendidos: 388, clientesNovos: 123, faturamentoProdutos: 5208, produtosVendidos: 49 },
          '2025-12': { faturamento: 101749, servicosRealizados: 801, clientesAtendidos: 424, clientesNovos: 107, faturamentoProdutos: 6174, produtosVendidos: 57 },
          '2026-1': { faturamento: 100259, servicosRealizados: 691, clientesAtendidos: 401, clientesNovos: 139, faturamentoProdutos: 4415, produtosVendidos: 37 },
          '2026-2': { faturamento: 95551, servicosRealizados: 646, clientesAtendidos: 327, clientesNovos: 99, faturamentoProdutos: 2937, produtosVendidos: 27 },
          '2026-3': { faturamento: 31530, servicosRealizados: 252, clientesAtendidos: 139, clientesNovos: 44, faturamentoProdutos: 500, produtosVendidos: 4 },
        },
        'unit-jardins': {
          '2026-2': { faturamento: 36530, servicosRealizados: 236, clientesAtendidos: 108, clientesNovos: 110, faturamentoProdutos: 950, produtosVendidos: 8 },
          '2026-3': { faturamento: 28708, servicosRealizados: 180, clientesAtendidos: 98, clientesNovos: 84, faturamentoProdutos: 993, produtosVendidos: 15 },
        },
      };
      const existingUP2 = JSON.parse(localStorage.getItem(KEYS.UNIT_PERFORMANCE)) || [];
      const updatedUP = existingUP2.map(r => {
        const unitData = AVEC_REAL_DATA[r.unitId];
        if (!unitData) return r;
        const monthData = unitData[r.year + '-' + r.month];
        if (!monthData) return r;
        const merged = { ...r, ...monthData };
        if (merged.clientesAtendidos > 0) {
          merged.ticketMedio = Math.round(merged.faturamento / merged.clientesAtendidos);
        }
        merged.comissao = Math.round(merged.faturamento * 0.40);
        merged.updatedAt = new Date().toISOString();
        return merged;
      });
      localStorage.setItem(KEYS.UNIT_PERFORMANCE, JSON.stringify(updatedUP));
    } catch {}
    // v9: Replace fictional professionals with real ones from Avec
    //
    // CRÍTICO: idempotente! Roda só UMA vez. Antes sobrescrevia TODOS
    // os profs/users/performance com SEED toda vez que schema bumpava —
    // apagava cadastros novos do admin e senhas trocadas pelos profs.
    try {
      const alreadyApplied = localStorage.getItem('_fk_v9_applied') === 'true';
      if (!alreadyApplied) {
        // CADASTRO_SEED_TRAVADO: o cadastro e os usuários não vêm mais do seed
        if (!SEM_SEED) {
          localStorage.setItem(KEYS.PROFESSIONALS, JSON.stringify(SEED_PROFESSIONALS));
          localStorage.setItem(KEYS.USERS, JSON.stringify(SEED_USERS));
        }
        localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
      }
      localStorage.setItem('_fk_v9_applied', 'true');
    } catch {}
    // v10-v11: Refresh performance + services with real Avec extras (report 0032 data)
    // Unifies sobrancelha + sobrancelha cera into single service, exact total distribution
    try {
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
      localStorage.setItem(KEYS.SERVICES, JSON.stringify(SEED_SERVICES));
    } catch {}
    // v13: Refresh performance with real Avec professional data (report 0021 - faturamento por profissional)
    try {
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
    } catch {}
    // v14: Refresh performance with real Avec commission data (report 0123)
    try {
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
    } catch {}
    // v15: Override unit performance with real Avec data + geracaoDemanda
    try {
      localStorage.setItem(KEYS.UNIT_PERFORMANCE, JSON.stringify(SEED_UNIT_PERFORMANCE));
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
    } catch {}
    // v16: Force refresh unit performance with geracaoDemanda field
    try {
      localStorage.setItem(KEYS.UNIT_PERFORMANCE, JSON.stringify(SEED_UNIT_PERFORMANCE));
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
    } catch {}
    // v17: geracaoDemanda = clientesNovos + semPreferencia (report 0326)
    try {
      localStorage.setItem(KEYS.UNIT_PERFORMANCE, JSON.stringify(SEED_UNIT_PERFORMANCE));
    } catch {}
    // v18: clientesAtendidos = visitas (not clientes) from Avec
    try {
      localStorage.setItem(KEYS.UNIT_PERFORMANCE, JSON.stringify(SEED_UNIT_PERFORMANCE));
    } catch {}
    // v19: agendamentosPreferenciais + semPreferencia from Avec report 0326
    try {
      localStorage.setItem(KEYS.UNIT_PERFORMANCE, JSON.stringify(SEED_UNIT_PERFORMANCE));
    } catch {}
    // v20: Merge depilação (nariz+orelha+nariz&orelha×2) into svc-depilacao, merge disfarce barba+cabelo into svc-disfarce
    try {
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
      localStorage.setItem(KEYS.SERVICES, JSON.stringify(SEED_SERVICES));
    } catch {}
    // v21: Add missing Lavagem Especial data from Avec
    try {
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
    } catch {}
    // v22: Zero out Jardins data before Feb 2026 + Reports uses unitPerformance
    try {
      localStorage.setItem(KEYS.UNIT_PERFORMANCE, JSON.stringify(SEED_UNIT_PERFORMANCE));
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
    } catch {}
    // v23: Zero out ALL professionals without Avec data for months where unit has Avec data
    try {
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
    } catch {}
    // v24: Fix unit comissao — sum real professional commissions instead of random %
    try {
      localStorage.setItem(KEYS.UNIT_PERFORMANCE, JSON.stringify(SEED_UNIT_PERFORMANCE));
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
    } catch {}
    // v25: Default password mudar123 + mustChangePassword for all professionals
    //
    // CRÍTICO: 3 camadas de proteção contra reset acidental de senhas:
    //   1. Marca local `_fk_v25_applied` — só roda UMA vez por device
    //   2. Pula se QUALQUER user não-admin já trocou senha
    //      (mustChangePassword === false) — sinal que o reset já rodou antes
    //   3. Pula se localStorage estiver vazio (boot recém com cloud — vai
    //      carregar dados frescos do Supabase em seguida; resetar agora
    //      apagaria senhas que vão ser recarregadas)
    try {
      const alreadyApplied = localStorage.getItem('_fk_v25_applied') === 'true';
      const users = JSON.parse(localStorage.getItem(KEYS.USERS)) || [];
      const someoneAlreadyChanged = users.some(u =>
        u.role !== 'admin' && u.mustChangePassword === false
      );
      const isFirstBoot = users.length === 0;
      if (!alreadyApplied && !someoneAlreadyChanged && !isFirstBoot) {
        const updated = users.map(u => {
          if (u.role !== 'admin') {
            return { ...u, password: 'mudar123', mustChangePassword: true };
          }
          return u;
        });
        localStorage.setItem(KEYS.USERS, JSON.stringify(updated));
      }
      // Marca como aplicada — qualquer caminho acima é one-time
      localStorage.setItem('_fk_v25_applied', 'true');
    } catch {}
    // v26: ⚠️ DESATIVADO — antes sobrescrevia role do prof/user com a do SEED
    // toda vez que a migration rodava. Quando admin mudava role (ex: Eduarda
    // Jardins de Assistente → Manicure), qualquer device que ainda estava em
    // schema antigo rodava v26 ao botar e REVERTIA pra Assistente do seed.
    //
    // Também re-adicionava profs do seed que tinham sido "excluídos" (mesmo
    // que soft-delete via active:false continue no array, qualquer reset
    // acidental do localStorage faria todos voltarem).
    //
    // Agora: NO-OP. As migrations já rodaram em todos devices em produção;
    // alterações manuais do admin sempre prevalecem.
    try {
      const v26Applied = localStorage.getItem('_fk_v26_applied') === 'true';
      if (!v26Applied) {
        localStorage.setItem('_fk_v26_applied', 'true');
      }
    } catch {}
    // v27: Update March 2026 Avec data (full month)
    try {
      localStorage.setItem(KEYS.UNIT_PERFORMANCE, JSON.stringify(SEED_UNIT_PERFORMANCE));
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
    } catch {}
    // v29: Fix commission data from report 0123 (correct values for March 2026)
    try {
      localStorage.setItem(KEYS.UNIT_PERFORMANCE, JSON.stringify(SEED_UNIT_PERFORMANCE));
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
    } catch {}
    // v30: Zero out fake pacotesVendidos for all professionals in months with real Avec data
    try {
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
    } catch {}
    // v31: Real package data for receptionists from Avec pacotes revenue
    try {
      localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(SEED_PERFORMANCE));
    } catch {}
    // v32: Aliases para matching Avec (Day=Daiane, Gringo=Eduardo Isaac, etc).
    // Sem isso, faturamento Avec não casa com o prof FK e fica zerado.
    // CADASTRO_SEED_TRAVADO: não grava mais o alias do seed por cima do que veio
    // do cofre (a leitura, getProfessionals, ainda completa o alias VAZIO, sem gravar).
    if (!SEM_SEED) try {
      const seedAliases = Object.fromEntries(
        SEED_PROFESSIONALS
          .filter(p => Array.isArray(p.aliases) && p.aliases.length > 0)
          .map(p => [p.id, p.aliases])
      );
      const profs = JSON.parse(localStorage.getItem(KEYS.PROFESSIONALS)) || [];
      const updated = profs.map(p => seedAliases[p.id] ? { ...p, aliases: seedAliases[p.id] } : p);
      localStorage.setItem(KEYS.PROFESSIONALS, JSON.stringify(updated));
    } catch {}
    localStorage.setItem(KEYS.SCHEMA_VERSION, CURRENT_SCHEMA_VERSION);
  }
  return migrated;
}

// Load all data from Supabase cloud and overwrite localStorage
// Returns true if cloud data was found and applied
//
// PROTEÇÃO ANTI-RACE: NÃO sobrescreve chaves que estão "dirty" (com upload
// pendente ou em curso). Sem isso, o auto-refresh sobrescreveria mudanças
// locais antes do upload terminar — bug clássico que faz exclusões/edições
// "não pegarem".
//
// Backfill: chaves que estão em CLOUD_DATA_KEYS mas não vieram do cloud
// E têm dados locais → faz push pra cloud (recupera dados que ainda não foram sincronizados,
// como desafios criados antes da chave ser adicionada à lista de sync).
// Chaves cujo array NUNCA pode encolher inesperadamente — se o cloud trouxer
// menos elementos que o local, NÃO sobrescreve (provável cloud stale ou
// upload de outro device falhando). Em vez disso, faz push do local pro cloud.
// Profissionais sumindo era exatamente esse bug: cloud com 12 profs
// sobrescrevia local com 13 que tinha o recém-cadastrado.
// ⚠️ fk_professionals, fk_prof_challenges, fk_services, fk_packages REMOVIDOS —
// a proteção "anti-shrinkage" estava causando regressão multi-device: device
// com cache antigo via cloud com menos itens (porque outro device acabou de
// excluir) e BLOQUEAVA o sync — empurrando a versão velha de volta → mudança
// do outro device era desfeita.
// Pra fk_services / fk_packages: aceitamos last-write-wins. Saves locais são
// instantâneos (cloudSave fire-and-forget na linha 560), então a janela de
// conflito é mínima.
// Chaves de cadastro críticas: cloud com MENOS items que local = sinal
// de cache antigo de outro device. Bloqueia load e empurra local de volta.
// Combinado com o merge por updatedAt + tombstone, deletes legítimos AINDA
// propagam (porque vêm marcados com deletedAt, não somem do array).
//
// fk_packages e fk_services REMOVIDOS — vivem em Postgres agora (Pacote C).
const ARRAY_KEYS_NO_SHRINK = new Set([
  'fk_users',
  'fk_units',
  'fk_unit_metas',
  'fk_unit_config',
  'fk_holidays',
  'fk_professionals',
  'fk_prof_challenges',
  // Pacote D — performance/metas históricas:
  'fk_performance',           // perf mensal por profissional
  'fk_unit_performance',      // perf mensal por unidade
  'fk_daily_revenue',         // receita diária por unidade
]);

// Decide se aplica dado do cloud sobre o local.
// Bloqueia "shrinkage" inesperado em arrays críticos de cadastro.
function shouldApplyCloudData(key, cloudData) {
  let local;
  try { local = JSON.parse(localStorage.getItem(key) || 'null'); }
  catch { return true; }

  // Arrays: bloqueia shrinkage (cloud com menos items que local).
  // SEED_TRAVADO: fk_daily_revenue sai desta trava (ela empurrava a cópia local
  // INTEIRA, com o seed, por cima do cofre). O merge por id abaixo já preserva
  // o que só existe no aparelho, e o servidor nunca derruba dia guardado.
  const semTravaEncolhimento = SEED_TRAVADO && key === KEYS.DAILY_REVENUE;
  if (!semTravaEncolhimento && ARRAY_KEYS_NO_SHRINK.has(key) && Array.isArray(cloudData) && Array.isArray(local)) {
    if (cloudData.length < local.length) {
      console.warn(`[loadFromCloud] BLOQUEADO ${key}: cloud=${cloudData.length} < local=${local.length}. Forçando push do local.`);
      cloudSave(key, local).catch(() => {});
      return false;
    }
  }
  // Objects (Pacote D): bloqueia perda de chaves
  if (MERGE_OBJECT_BY_UPDATED_AT_KEYS.has(key) &&
      cloudData && typeof cloudData === 'object' && !Array.isArray(cloudData) &&
      local && typeof local === 'object' && !Array.isArray(local)) {
    const cloudKeys = Object.keys(cloudData).length;
    const localKeys = Object.keys(local).length;
    if (cloudKeys < localKeys) {
      console.warn(`[loadFromCloud] BLOQUEADO ${key}: cloud=${cloudKeys}k < local=${localKeys}k. Forçando push do local.`);
      cloudSave(key, local).catch(() => {});
      return false;
    }
  }
  return true;
}

// Merge inteligente item-por-item para chaves de cadastro (profs, units, etc).
// Mantém o item com `updatedAt` MAIS RECENTE entre cloud e local — evita
// que outro device com versão antiga sobrescreva uma mudança recente local.
// Items sem `updatedAt` em ambos lados: cloud vence (comportamento antigo).
//
// PACOTE A (set ampliado): packages/services/holidays também entram aqui.
// Antes: array inteiro substituía → device com cache antigo apagava itens
// recém-cadastrados. Agora: cada item é versionado, merge resolve conflito
// item-por-item, deletes são detectados por tombstone (deletedAt).
const MERGE_BY_UPDATED_AT_KEYS = new Set([
  'fk_professionals',
  'fk_units',
  'fk_unit_config',
  'fk_unit_metas',
  'fk_prof_challenges',
  'fk_holidays',
  // Pacote D — arrays de performance histórica
  'fk_performance',
  'fk_unit_performance',
  'fk_daily_revenue',
  // fk_packages e fk_services REMOVIDOS — viraram tabelas Postgres (Pacote C)
]);

// Pacote D — chaves cujo cloud é OBJETO (não array). Cada chave-filha tem
// seu próprio updatedAt. Merge: união das chaves, vence o mais recente.
// Exemplo: fk_weekly_duels = { '2026-W22': { updatedAt, pairs, ... } }
const MERGE_OBJECT_BY_UPDATED_AT_KEYS = new Set([
  'fk_weekly_duels',
  'fk_achievements_v1',
  'fk_engagement_v1',
]);

function mergeObjectByUpdatedAt(cloudObj, localObj) {
  const ts = (it) => {
    const v = it?.updatedAt;
    if (!v) return 0;
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : 0;
  };
  const isObj = (x) => x && typeof x === 'object' && !Array.isArray(x);
  if (!isObj(cloudObj) || !isObj(localObj)) return cloudObj;
  const now = Date.now();
  const out = { ...cloudObj };
  for (const [k, lv] of Object.entries(localObj)) {
    const cv = cloudObj[k];
    if (cv === undefined) {
      // Existe só local (criado aqui, ainda não subiu) → preserva
      out[k] = lv;
      continue;
    }
    if (!isObj(lv) || !isObj(cv)) {
      // Tipos primitivos — last-write-wins padrão (cloud já está em out)
      continue;
    }
    const tl = ts(lv);
    const tc = ts(cv);
    // Edits muito recentes (<5min) sempre vencem
    if (tl > 0 && (now - tl) < FRESH_LOCAL_WINDOW_MS) { out[k] = lv; continue; }
    out[k] = tl > tc ? lv : cv;
  }
  return out;
}

// Janela em que edits locais SEMPRE vencem o cloud, independente do timestamp
// do cloud. Protege contra:
//   - Race entre cloudSave em curso e auto-refresh em outro device
//   - Cloud com timestamp anômalo (relógio errado em outro device)
//   - Push acidental de versão antiga por aba aberta há tempo
// Após esse janela volta ao comportamento normal (vence o mais recente).
const FRESH_LOCAL_WINDOW_MS = 5 * 60 * 1000; // 5 min

// MERGE_JANELA_SO_LOCAL (29/10/2026, Meta de Moema): a janela vale só para o item
// GRAVADO NESTE APARELHO nesta sessão. Antes valia para qualquer item com carimbo
// recente, inclusive o que tinha acabado de vir do cofre: o dono trocou a meta
// 400 → 340 mil em menos de 5 min e o aparelho da recepcionista manteve o 400
// (o empurrão dela volta 403 e o ETag do 340 era confirmado: preso para sempre).
// Mapa `chave|id` → updatedAt gravado, preenchido no set() (as gravações locais);
// o loadFromCloud grava direto no localStorage e não passa por aqui.
const _gravadosAqui = new Map();
const chaveDoMerge = (key) => ((PERF_MERGE_POR_UNIDADE && key === KEYS.PERFORMANCE) ? chavePerfMerge : (x) => x.id);
function marcarGravadosAqui(key, data) {
  if (!MERGE_JANELA_SO_LOCAL || !MERGE_BY_UPDATED_AT_KEYS.has(key) || !Array.isArray(data)) return;
  let antes;
  try { antes = JSON.parse(localStorage.getItem(key) || '[]'); } catch { antes = []; }
  const chaveDe = chaveDoMerge(key);
  const carimboAntes = new Map((Array.isArray(antes) ? antes : []).filter(x => x?.id).map(x => [chaveDe(x), x.updatedAt]));
  for (const it of data) {
    if (!it?.id || !it.updatedAt) continue;
    const k = chaveDe(it);
    if (carimboAntes.get(k) !== it.updatedAt) _gravadosAqui.set(`${key}|${k}`, it.updatedAt);
  }
}
// Envio recusado pelo servidor (403): a edição daqui não vale como protegida.
const gravadoAqui = (key, k, item) => !envioRecusado(key) && _gravadosAqui.get(`${key}|${k}`) === item?.updatedAt;

// `chaveDe` (23/10/2026): identidade do item no merge. Padrão = id (como sempre).
// fk_performance com PERF_MERGE_POR_UNIDADE = id + unidade: a mesma pessoa em
// duas unidades no mês tinha o MESMO id nos dois registros (Kayk: Moema e
// Oscar) e o Map por id casava os dois com o mesmo item local (um sumia).
// `opts` (MERGE_JANELA_SO_LOCAL): { key } liga a janela só ao que foi gravado
// aqui; `stats.manteveMaisVelho` = a janela manteve um item local mais velho que
// o do cofre (quem chama não confirma o ETag: a próxima leitura confere de novo).
function mergeArrayByUpdatedAt(cloudArr, localArr, chaveDe = (x) => x.id, opts = {}) {
  if (!Array.isArray(cloudArr) || !Array.isArray(localArr)) return cloudArr;
  const soLocal = MERGE_JANELA_SO_LOCAL && typeof opts.key === 'string';
  const ts = (it) => {
    const v = it?.updatedAt;
    if (!v) return 0;
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : 0;
  };
  const now = Date.now();
  const localById = new Map(localArr.filter(x => x?.id).map(x => [chaveDe(x), x]));
  const out = cloudArr.map(c => {
    if (!c?.id) return c;
    const l = localById.get(chaveDe(c));
    if (!l) return c;

    // 🪦 TOMBSTONE: se ambos lados têm deletedAt, vence o mais recente.
    // Se SÓ um lado tem, esse vence (o delete tem que propagar — senão
    // outro device ressuscita o item com cache antigo).
    if (l.deletedAt || c.deletedAt) {
      const tl = ts({ updatedAt: l.deletedAt || l.updatedAt });
      const tc = ts({ updatedAt: c.deletedAt || c.updatedAt });
      return tl >= tc ? l : c;
    }

    const tl = ts(l);
    const tc = ts(c);
    // Edits MUITO recentes (5min) sempre vencem — protege a janela de save.
    // MERGE_JANELA_SO_LOCAL: só a edição feita neste aparelho.
    if (tl > 0 && (now - tl) < FRESH_LOCAL_WINDOW_MS && (!soLocal || gravadoAqui(opts.key, chaveDe(l), l))) {
      if (soLocal && tc > tl && opts.stats) opts.stats.manteveMaisVelho = true;
      return l;
    }
    // Senão, vence quem tem updatedAt mais recente.
    return tl > tc ? l : c;
  });
  // Items que existem só local (criados aqui mas ainda não subiram) → preserva
  const cloudIds = new Set(cloudArr.filter(x => x?.id).map(x => chaveDe(x)));
  for (const l of localArr) {
    if (l?.id && !cloudIds.has(chaveDe(l))) out.push(l);
  }
  return out;
}

// PERF_MERGE_POR_UNIDADE (23/10/2026, rodada 2, item 2): o registro de
// fk_performance é (profissional, unidade, ano, mês), com o id antigo ou o novo
// com a unidade (SYNC_ID_COM_UNIDADE). Quem decide as unidades da pessoa no mês
// é o script do Mac: registro local de (profissional, ano, mês) que o cofre já
// tem, mas com id + unidade que o cofre NÃO tem, sai da cópia local antes da
// trava de encolhimento e do merge. Cobre o id antigo × id novo (inclusive o
// passe parcial, com uma unidade de id antigo e a outra de id novo: o id antigo
// é o MESMO nas duas unidades, então comparar só o id não bastava), a unidade
// que o script tirou e o registro que o dedupe dele derrubou. É a regra do
// servidor (mergePerformance em api/_lib/data-merge.js): o que sai daqui o
// servidor recusaria. Sem isso ele ficaria como "item só do aparelho" para
// sempre (a equipe recebe 403 ao empurrar), dobrando as somas e congelando o
// aparelho na trava de encolhimento. Pessoa e mês que o cofre não tem ficam (o
// aparelho ainda devolve o que sumiu do cofre inteiro, como antes). Id perf-*
// (seed e Lançar Dados) fica como sempre.
export const chavePerfMerge = (x) => `${x.id}|${x.unitId ?? ''}`;
const pessoaMesPerf = (x) => `${x?.professionalId}|${x?.year}|${x?.month}`;
const perfSemantico = (x) => !!(x && x.id && !String(x.id).startsWith('perf-') && x.professionalId && x.year != null && x.month != null);
export function normalizarPerfLocal(cloudData) {
  if (!PERF_MERGE_POR_UNIDADE || !Array.isArray(cloudData)) return 0;
  let local;
  try { local = JSON.parse(localStorage.getItem(KEYS.PERFORMANCE) || 'null'); } catch { return 0; }
  if (!Array.isArray(local)) return 0;
  const chavesMergeNuvem = new Set(cloudData.filter(x => x?.id).map(chavePerfMerge));
  const pessoasMesNuvem = new Set(cloudData.filter(perfSemantico).map(pessoaMesPerf));
  const limpo = local.filter(l => !(perfSemantico(l) && !chavesMergeNuvem.has(chavePerfMerge(l)) && pessoasMesNuvem.has(pessoaMesPerf(l))));
  const tirados = local.length - limpo.length;
  if (tirados > 0) {
    try { localStorage.setItem(KEYS.PERFORMANCE, JSON.stringify(limpo)); } catch { return 0; }
  }
  return tirados;
}

// SEED_TRAVADO (23/10/2026): item LOCAL com o carimbo do seed que o cofre NÃO
// tem sai da cópia local antes da trava de encolhimento e do merge. Sem isso:
// (1) o merge devolvia o seed ao cofre como "item só do aparelho"; (2) quando
// as metas falsas forem apagadas do cofre, todo aparelho que ainda as tem
// ficaria com mais itens que o cofre e a trava de encolhimento o congelaria
// nessa cópia (a equipe recebe 403 ao empurrar e nunca mais aplicaria o cofre).
// Só fk_daily_revenue e fk_unit_metas (as duas que pararam de ser semeadas).
const CHAVES_LIMPA_SEED_LOCAL = new Set([KEYS.DAILY_REVENUE, KEYS.UNIT_METAS]);
export function limparSeedLocal(key, cloudData) {
  if (!SEED_TRAVADO || !CHAVES_LIMPA_SEED_LOCAL.has(key) || !Array.isArray(cloudData)) return 0;
  let local;
  try { local = JSON.parse(localStorage.getItem(key) || 'null'); } catch { return 0; }
  if (!Array.isArray(local)) return 0;
  const idsNuvem = new Set(cloudData.filter(x => x?.id).map(x => x.id));
  const limpo = local.filter(it => !(it && ehCarimboSeed(it.updatedAt) && !idsNuvem.has(it.id)));
  const tirados = local.length - limpo.length;
  if (tirados > 0) {
    try { localStorage.setItem(key, JSON.stringify(limpo)); } catch { return 0; }
  }
  return tirados;
}

// CADASTRO_SEED_TRAVADO (28/10/2026): o que o cofre de fato respondeu NESTA
// sessão (página aberta). `aplicadas` = a cópia do cofre foi gravada no aparelho
// (o loadFromCloud aplicou a chave; não vale o cloudLoaded, que vira true no
// tempo esgotado, no "não mudou" e na chave suja). `conferidas` = aplicada, "não
// mudou" (o ETag só fica guardado depois de aplicar, e o initStorage esquece o
// ETag da chave que ele mexeu) ou o cofre disse que a chave não existe.
const _aplicadasNestaSessao = new Set();
const _conferidasNestaSessao = new Set();
export const chaveAplicadaDoCofre = (key) => _aplicadasNestaSessao.has(key);
export const chaveConferidaComOCofre = (key) => _conferidasNestaSessao.has(key) || _aplicadasNestaSessao.has(key);

// CADASTRO_SEED_TRAVADO, revisão de 28/10/2026: no cadastro e nos dias fechados
// (as duas chaves com portão: a descoberta de CPF e o Bloquear/Desbloquear dia)
// o "não mudou" (304) só vale como APLICADO quando a cópia do aparelho é,
// caractere por caractere, a que foi aplicada do cofre (impressão gravada ao
// aplicar). Antes a descoberta de CPF ficava parada em toda recarga normal (o
// 304 nunca contava). Cópia diferente (app antigo mexeu nela, edição que subiu e
// voltou do servidor, aparelho sem a impressão ainda): o ETag é esquecido e a
// chave é relida inteira na mesma hora.
const CHAVES_COPIA_CONFERIDA = new Set([KEYS.PROFESSIONALS, KEYS.HOLIDAYS]);
const MARCA_COPIA_APLICADA = '__cloud_aplicado_';
function impressaoTexto(texto) {
  let h = 0x811c9dc5;
  for (let i = 0; i < texto.length; i++) { h ^= texto.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return `${texto.length}:${(h >>> 0).toString(16)}`;
}
function marcarCopiaAplicada(key) {
  if (!SEM_SEED || !CHAVES_COPIA_CONFERIDA.has(key)) return;
  try { localStorage.setItem(MARCA_COPIA_APLICADA + key, impressaoTexto(localStorage.getItem(key) ?? '')); } catch { /* sem a marca: a próxima leitura vem cheia */ }
}
function copiaIgualAAplicada(key) {
  try {
    const marca = localStorage.getItem(MARCA_COPIA_APLICADA + key);
    return !!marca && marca === impressaoTexto(localStorage.getItem(key) ?? '');
  } catch { return false; }
}

// CADASTRO_SEED_TRAVADO, revisão de 28/10/2026: item LOCAL sem updatedAt que o
// cofre não tem sai da cópia local (cadastro, unidades, dias fechados) antes da
// trava de encolhimento e do merge. Todo cadastro, unidade e dia de verdade
// carimba ao nascer; sem carimbo e fora do cofre é o seed que um app antigo pôs
// no aparelho ("merge de novos profissionais"). Sem isso ele ficava como "item só
// do aparelho" e a descoberta de CPF podia subi-lo de volta.
const CHAVES_SEM_CARIMBO_LOCAL = new Set([KEYS.PROFESSIONALS, KEYS.UNITS, KEYS.HOLIDAYS]);
const temCarimbo = (it) => { const t = new Date(it?.updatedAt || 0).getTime(); return Number.isFinite(t) && t > 0; };
export function limparCopiaSemCarimboLocal(key, cloudData) {
  if (!SEM_SEED || !CHAVES_SEM_CARIMBO_LOCAL.has(key) || !Array.isArray(cloudData)) return 0;
  let local;
  try { local = JSON.parse(localStorage.getItem(key) || 'null'); } catch { return 0; }
  if (!Array.isArray(local)) return 0;
  const idsNuvem = new Set(cloudData.filter(x => x?.id).map(x => x.id));
  const limpo = local.filter(it => !(it && it.id && !idsNuvem.has(it.id) && !temCarimbo(it)));
  const tirados = local.length - limpo.length;
  if (tirados > 0) {
    try { localStorage.setItem(key, JSON.stringify(limpo)); } catch { return 0; }
  }
  return tirados;
}

export async function loadFromCloud() {
  try {
    const cloudData = await cloudLoadAll();
    const cloudKeys = Object.keys(cloudData);
    let applied = 0;
    const reler = [];
    cloudKeys.forEach(key => { if (aplicarChaveDoCofre(key, cloudData[key], reler)) applied++; });
    // Cópia diferente da aplicada com "não mudou" (CHAVES_COPIA_CONFERIDA): o
    // ETag já foi esquecido, a leitura vem cheia.
    for (const key of reler) {
      let valor;
      try { valor = await cloudLoad(key); } catch { valor = CLOUD_UNAVAILABLE; }
      if (valor === null || valor === CLOUD_UNAVAILABLE || valor === CLOUD_NOT_MODIFIED) continue;
      if (aplicarChaveDoCofre(key, valor, null)) applied++;
    }
    // Backfill: pra cada chave esperada mas ausente no cloud, se tiver dados locais → push
    for (const key of CLOUD_DATA_KEYS) {
      if (cloudKeys.includes(key)) continue;
      _conferidasNestaSessao.add(key);   // o cofre respondeu: a chave não existe
      // SEED_TRAVADO: o faturamento diário nunca sobe por backfill (a cópia
      // local podia ser o seed de um aparelho com o armazenamento vazio)
      if (SEED_TRAVADO && key === KEYS.DAILY_REVENUE) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const data = JSON.parse(raw);
        const hasContent = Array.isArray(data) ? data.length > 0 : Object.keys(data || {}).length > 0;
        if (hasContent) cloudSave(key, data).catch(() => {});
      } catch {}
    }
    return applied > 0;
  } catch {
    return false;
  }
}

// Uma chave do cofre (o corpo do loadFromCloud). true = aplicou. `reler` (lista)
// recebe a chave com "não mudou" cuja cópia não é a aplicada; null = não relê.
function aplicarChaveDoCofre(key, valor, reler) {
  // 0) Storage retornou 304 Not Modified — arquivo igual ao cache local,
  //    NÃO reaplica (poupa CPU/storage write desnecessário).
  if (valor === CLOUD_NOT_MODIFIED) {
    if (isDirty(key)) return false;
    if (SEM_SEED && CHAVES_COPIA_CONFERIDA.has(key)) {
      if (copiaIgualAAplicada(key)) { _conferidasNestaSessao.add(key); _aplicadasNestaSessao.add(key); }
      else if (reler) { esquecerCloudMeta(key); reler.push(key); }
      return false;
    }
    _conferidasNestaSessao.add(key);
    return false;
  }
  const cloudData = { [key]: valor };
  // 0b) Não deu pra consultar (sem sessão / servidor fora): não mexe no
  //     local e NÃO conta como ausente (sem backfill).
  if (cloudData[key] === CLOUD_UNAVAILABLE) return false;
  // 1) Pula chaves com upload pendente — sobrescrevê-las apagaria mudanças locais
  if (isDirty(key)) return false;
  // 1b) SEED_TRAVADO: seed local que o cofre não tem sai da cópia local
  limparSeedLocal(key, cloudData[key]);
  // 1b') CADASTRO_SEED_TRAVADO: item local sem carimbo que o cofre não tem sai
  limparCopiaSemCarimboLocal(key, cloudData[key]);
  // 1c) PERF_MERGE_POR_UNIDADE: registro local com id antigo que o cofre tem com o id novo
  if (key === KEYS.PERFORMANCE) normalizarPerfLocal(cloudData[key]);
  // 2) Bloqueia se cloud trouxer menos itens que local em chaves críticas
  if (!shouldApplyCloudData(key, cloudData[key])) return false;
  // 3) Merge inteligente em chaves de cadastro (resolve conflito por updatedAt)
  let toApply = cloudData[key];
  const statsMerge = {};
  if (MERGE_BY_UPDATED_AT_KEYS.has(key)) {
    try {
      const localArr = JSON.parse(localStorage.getItem(key) || '[]');
      toApply = (PERF_MERGE_POR_UNIDADE && key === KEYS.PERFORMANCE)
        ? mergeArrayByUpdatedAt(cloudData[key], localArr, chavePerfMerge, { key, stats: statsMerge })
        : mergeArrayByUpdatedAt(cloudData[key], localArr, undefined, { key, stats: statsMerge });
      // Cadastro (06/10/2026 — campos sensíveis por escopo): o servidor
      // manda a cópia PROJETADA (salário/CPF/contato só pra quem pode).
      // A cópia local antiga, COMPLETA, é SUBSTITUÍDA nos itens marcados —
      // nunca mesclada preservando os sensíveis (utils/cadastroSensivel.js).
      if (key === KEYS.PROFESSIONALS) toApply = reprojetarLocal(toApply, cloudData[key]);
      // Se merge resultou em algo diferente do cloud → empurra de volta
      if (JSON.stringify(toApply) !== JSON.stringify(cloudData[key])) {
        cloudSave(key, toApply).catch(() => {});
      }
    } catch { /* fallback: usa cloud puro */ }
  }
  // 3b) Merge inteligente em chaves OBJETO (weekly_duels, achievements,
  //     engagement). União por chave-filha, updatedAt mais recente vence.
  else if (MERGE_OBJECT_BY_UPDATED_AT_KEYS.has(key)) {
    try {
      const localObj = JSON.parse(localStorage.getItem(key) || '{}');
      toApply = mergeObjectByUpdatedAt(cloudData[key], localObj);
      if (JSON.stringify(toApply) !== JSON.stringify(cloudData[key])) {
        cloudSave(key, toApply).catch(() => {});
      }
    } catch { /* fallback */ }
  }
  localStorage.setItem(key, JSON.stringify(toApply));
  // Só agora persiste o ETag — o dado foi APLICADO de verdade. Quando o
  // apply é pulado (dirty/shrink), o meta fica pendente e o próximo poll
  // refetcha inteiro, em vez de receber 304 de uma versão nunca aplicada
  // (era isso que congelava um device no passado mesmo após reload).
  // MERGE_JANELA_SO_LOCAL: a janela manteve um item daqui mais velho que o do
  // cofre → sem ETag, a próxima leitura vem cheia e confere de novo.
  if (!statsMerge.manteveMaisVelho) confirmCloudMeta(key);
  marcarCopiaAplicada(key);
  _aplicadasNestaSessao.add(key);
  return true;
}

// Push all current localStorage data to cloud (for initial seeding)
export async function pushToCloud() {
  try {
    await cloudSaveAll();
    return true;
  } catch {
    return false;
  }
}

export function resetStorage() {
  Object.values(KEYS).forEach(k => localStorage.removeItem(k));
  initStorage();
}

// SEED_TRAVADO (23/10/2026): o "Resetar Dados" das Configurações passa a só
// trocar a cópia LOCAL pela do cofre. Nunca semeia, nunca envia nada:
//   - com upload pendente neste aparelho, não mexe (a edição ainda está subindo);
//   - lê o cofre ANTES de apagar qualquer coisa; se alguma chave não respondeu,
//     nada muda no aparelho (sem cópia vazia esperando o cofre);
//   - chave que o cofre não tem fica com a cópia local (não some dado);
//   - fk_users, catálogo e caches do Avec ficam como estão.
const CHAVES_CACHE_LOCAL = [
  KEYS.UNITS, KEYS.PROFESSIONALS, KEYS.PERFORMANCE, KEYS.UNIT_PERFORMANCE,
  KEYS.DAILY_REVENUE, KEYS.UNIT_METAS, KEYS.HOLIDAYS, KEYS.UNIT_CONFIG,
  KEYS.PROF_CHALLENGES, KEYS.WEEKLY_DUELS,
];
export async function limparCacheLocal() {
  if (getDirtyKeys().size > 0) return { ok: false, motivo: 'pendente' };
  // Sem o ETag guardado o cofre manda a cópia inteira (e não "não mudou")
  for (const k of CHAVES_CACHE_LOCAL) esquecerCloudMeta(k);
  let nuvem;
  try { nuvem = await cloudLoadAll(); } catch { return { ok: false, motivo: 'cofre', faltou: [...CHAVES_CACHE_LOCAL] }; }
  const faltou = CHAVES_CACHE_LOCAL.filter(k => nuvem[k] === CLOUD_UNAVAILABLE || nuvem[k] === CLOUD_NOT_MODIFIED);
  if (faltou.length) return { ok: false, motivo: 'cofre', faltou };
  if (getDirtyKeys().size > 0) return { ok: false, motivo: 'pendente' };
  let trocadas = 0;
  for (const k of CHAVES_CACHE_LOCAL) {
    if (!Object.prototype.hasOwnProperty.call(nuvem, k)) continue;
    try { localStorage.setItem(k, JSON.stringify(nuvem[k])); } catch { return { ok: false, motivo: 'armazenamento', trocadas }; }
    confirmCloudMeta(k);
    marcarCopiaAplicada(k);
    _aplicadasNestaSessao.add(k);
    trocadas++;
  }
  return { ok: true, trocadas };
}

function get(key) {
  try { return JSON.parse(localStorage.getItem(key)) || []; } catch { return []; }
}
function set(key, data) {
  marcarGravadosAqui(key, data);
  localStorage.setItem(key, JSON.stringify(data));
  // Fire-and-forget cloud sync
  cloudSave(key, data).catch(() => {});
}

export const getUsers = () => get(KEYS.USERS);
export const getUnits = () => get(KEYS.UNITS);

// Aliases dos profs (Day, Gringo, Anna Jullya...) ficam no seed e podem ser
// sobrescritos pelo cloud sync (que tem snapshot antigo sem aliases). Aplica
// merge no read pra garantir que o matcher Avec sempre veja os aliases.
const SEED_PRO_ALIASES = Object.fromEntries(
  SEED_PROFESSIONALS
    .filter(p => Array.isArray(p.aliases) && p.aliases.length > 0)
    .map(p => [p.id, p.aliases])
);
// CADASTRO_SEED_TRAVADO (28/10/2026): quem GRAVA o cadastro (updateProfessional,
// addProfessional, deleteProfessional) lê a cópia crua: o alias do seed que a
// leitura abaixo completa não pode subir para o cofre por carona.
export const getProfessionalsCru = () => get(KEYS.PROFESSIONALS);
export const getProfessionals = () => {
  const stored = get(KEYS.PROFESSIONALS);
  return stored.map(p =>
    SEED_PRO_ALIASES[p.id] && (!Array.isArray(p.aliases) || p.aliases.length === 0)
      ? { ...p, aliases: SEED_PRO_ALIASES[p.id] }
      : p
  );
};
// ─── Gravações do cadastro (eram o corpo dos callbacks do DataContext) ───────
// Devolvem a lista gravada no aparelho (o cloudSave sobe em seguida).
// CADASTRO_SEED_TRAVADO (28/10/2026): leem a cópia CRUA; false = getProfessionals.
const lerCadastroParaGravar = () => (CADASTRO_SEED_TRAVADO ? getProfessionalsCru() : getProfessionals());

export function gravarProfissionalNovo(professional) {
  const stamped = { ...professional, updatedAt: new Date().toISOString() };
  const updated = [...lerCadastroParaGravar(), stamped];
  saveProfessionals(updated);
  return updated;
}

export function gravarEdicaoProfissional(professional, opts = {}) {
  const all = lerCadastroParaGravar();
  // Stamp updatedAt — usado pelo merge cloud→local em loadFromCloud pra
  // resolver conflitos quando outro device subiu uma versão MAIS ANTIGA.
  //
  // opts.silent: NÃO carimba updatedAt. Usado em atualizações automáticas
  // (ex: discovery de CPF via Avec) pra evitar mass-stamping de 40+ profs
  // que faz a versão "atual" do device sobrescrever updates legítimos
  // de outro device. Sem silent, o discovery virava um vetor de conflito
  // que reverteu edições reais do admin (role, active=false, etc).
  // 19/10/2026: opts.campos = só os campos que a pessoa mudou. Eles vão por
  // cima do registro ATUAL do aparelho e seguem marcados em _campos para o
  // servidor aplicar só eles (uma cópia velha no aparelho não desfaz o resto).
  const campos = Array.isArray(opts.campos) ? opts.campos.filter(c => typeof c === 'string' && c !== 'id') : null;
  const updated = all.map(p => {
    if (p.id !== professional.id) return p;
    let merged;
    if (campos) {
      merged = { ...p };
      for (const c of campos) if (Object.prototype.hasOwnProperty.call(professional, c)) merged[c] = professional[c];
      merged._campos = [...new Set([...(Array.isArray(p._campos) ? p._campos : []), ...campos])];
    } else {
      merged = { ...p, ...professional };
    }
    if (!opts.silent) merged.updatedAt = new Date().toISOString();
    return merged;
  });
  saveProfessionals(updated);
  return updated;
}

// Soft-delete com tombstone — sync respeita cross-device
export function gravarExclusaoProfissional(professionalId) {
  const all = lerCadastroParaGravar();
  const now = new Date().toISOString();
  const updated = all.map(p => p.id === professionalId
    ? { ...p, deletedAt: now, updatedAt: now, active: false,
        _campos: [...new Set([...(Array.isArray(p._campos) ? p._campos : []), 'deletedAt', 'active'])] }
    : p
  );
  saveProfessionals(updated);
  return updated;
}

export const getPerformance = () => get(KEYS.PERFORMANCE);
export const getServices = () => get(KEYS.SERVICES);
export const getPackages = () => get(KEYS.PACKAGES);

export const saveUsers = (data) => set(KEYS.USERS, data);
export const saveUnits = (data) => set(KEYS.UNITS, data);
export const saveProfessionals = (data) => set(KEYS.PROFESSIONALS, data);
export const savePerformance = (data) => set(KEYS.PERFORMANCE, data);
export const saveServices = (data) => set(KEYS.SERVICES, data);
export const savePackages = (data) => set(KEYS.PACKAGES, data);
export const getUnitPerformance = () => get(KEYS.UNIT_PERFORMANCE);
export const saveUnitPerformance = (data) => set(KEYS.UNIT_PERFORMANCE, data);

export const getDailyRevenue = () => get(KEYS.DAILY_REVENUE);
export const saveDailyRevenue = (data) => set(KEYS.DAILY_REVENUE, data);
export const getUnitMetas = () => get(KEYS.UNIT_METAS);
export const saveUnitMetas = (data) => set(KEYS.UNIT_METAS, data);

// Desafios por profissional: { id, professionalId, year, month, fatDesafio, servicosDesafio: {svcId: qtd}, produtosDesafio: qtd }
export const getProfChallenges = () => get(KEYS.PROF_CHALLENGES) || [];
export const saveProfChallenges = (data) => set(KEYS.PROF_CHALLENGES, data);
export function upsertProfChallenge(record) {
  const all = getProfChallenges();
  const idx = all.findIndex(r => r.id === record.id);
  if (idx >= 0) all[idx] = { ...all[idx], ...record, updatedAt: new Date().toISOString() };
  else all.push({ ...record, updatedAt: new Date().toISOString() });
  saveProfChallenges(all);
  return record;
}
// 🪦 Soft-delete com tombstone — sync respeita cross-device.
// Antes era filter (delete físico) → cache antigo de outro device
// ressuscitava o desafio. Agora marca deletedAt; merge propaga o delete.
export function deleteProfChallenge(id) {
  const all = getProfChallenges();
  const now = new Date().toISOString();
  const updated = all.map(r => r.id === id
    ? { ...r, deletedAt: now, updatedAt: now }
    : r
  );
  saveProfChallenges(updated);
}
export const getHolidays = () => get(KEYS.HOLIDAYS);
export const saveHolidays = (data) => set(KEYS.HOLIDAYS, data);

// "Bloquear/Desbloquear dia" (Metas): era o corpo do saveHolidaysList do
// DataContext. Devolve false (nada gravado) quando CADASTRO_SEED_TRAVADO e os
// dias fechados ainda não foram conferidos com o cofre nesta sessão: sem o seed,
// a lista do aparelho pode estar vazia só porque o cofre não respondeu, e
// gravá-la apagaria os dias guardados (no servidor a lista que chega manda, por
// causa do "Desbloquear dia", que tira o dia da lista).
export function gravarDiasFechados(list) {
  if (CADASTRO_SEED_TRAVADO && !chaveConferidaComOCofre(KEYS.HOLIDAYS)) return false;
  // Carimba updatedAt em cada holiday que mudou (ou todos os novos)
  // pra o merge cross-device funcionar item-por-item.
  const now = new Date().toISOString();
  const prev = getHolidays();
  const prevById = new Map((prev || []).filter(h => h?.id).map(h => [h.id, h]));
  const stamped = (list || []).map(h => {
    if (!h?.id) return { ...h, id: 'hol-' + Math.random().toString(36).slice(2, 9), updatedAt: now };
    const old = prevById.get(h.id);
    if (!old) return { ...h, updatedAt: now, createdAt: h.createdAt || now };
    // Se nada mudou, preserva updatedAt (não bate em todos sem motivo)
    const changed = JSON.stringify({ ...old, updatedAt: undefined }) !==
                    JSON.stringify({ ...h, updatedAt: undefined });
    return changed ? { ...h, updatedAt: now } : old;
  });
  saveHolidays(stamped);
  return true;
}

// CADASTRO_SEED_TRAVADO, revisão de 28/10/2026: o Bloquear e o Desbloquear dia
// são UMA operação sobre a cópia APLICADA do aparelho (getHolidays), nunca sobre
// a lista da tela. Num boot que passa dos 12 s o estado da tela fica [] e o
// loadFromCloud aplica o cofre depois em segundo plano: a tela vazia + o dia
// novo apagava todos os dias guardados. Mesma trava do gravarDiasFechados
// (false = os dias ainda não foram conferidos com o cofre nesta sessão).
export function bloquearDia(dia) {
  if (!dia || typeof dia !== 'object' || !dia.id) return false;
  if (CADASTRO_SEED_TRAVADO && !chaveConferidaComOCofre(KEYS.HOLIDAYS)) return false;
  const atual = getHolidays();
  if (atual.some(h => h?.id === dia.id && !h?.deletedAt)) return true;   // já está bloqueado
  return gravarDiasFechados([...atual.filter(h => h?.id !== dia.id), dia]);
}
export function desbloquearDia(id) {
  if (!id) return false;
  if (CADASTRO_SEED_TRAVADO && !chaveConferidaComOCofre(KEYS.HOLIDAYS)) return false;
  const atual = getHolidays();
  if (!atual.some(h => h?.id === id)) return true;   // já não está
  return gravarDiasFechados(atual.filter(h => h?.id !== id));
}

// ─── Weekly duels ──────────────────────────────────────────────────────────
// Estrutura: { 'YYYY-Www': { weekStart, weekSat, pairs: [...], finalWinners: {...} } }
// IMPORTANTE: NÃO usa o get() padrão pq ele retorna [] como default — isso
// quebra pra dados em formato OBJETO (string-keyed). Aqui faz o parse direto
// e cai pra {} se não tem nada.
export function getWeeklyDuels() {
  try {
    const raw = localStorage.getItem(KEYS.WEEKLY_DUELS);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Defensivo: se por algum motivo veio array, descarta e retorna objeto
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
  } catch { return {}; }
}
export function saveWeeklyDuels(data) {
  // Garante que está salvando objeto (não array)
  const safe = (data && typeof data === 'object' && !Array.isArray(data)) ? data : {};
  localStorage.setItem(KEYS.WEEKLY_DUELS, JSON.stringify(safe));
  cloudSave(KEYS.WEEKLY_DUELS, safe).catch(() => {});
}
export function upsertWeeklyDuel(weekKey, duelData) {
  const all = getWeeklyDuels();
  all[weekKey] = { ...(all[weekKey] || {}), ...duelData, updatedAt: new Date().toISOString() };
  saveWeeklyDuels(all);
  return all[weekKey];
}

// ─── Defaults de comissão escalonada por unidade (Barbeiros) ─────────────────
// Hidrata registros antigos no localStorage/cloud que ainda não têm
// commissionTiers — assim usuários existentes não precisam reabrir o cadastro
// pra ter as taxas certas. Novas unidades também herdam esses defaults.
// Faixas vigentes desde a produção de nov/2026 (+4k, dono 01/11/2026).
const _COMM_3 = [
  { until: 27000, percent: 35 },
  { until: 40000, percent: 37 },
  { until: null,  percent: 39 },
];
const _COMM_4 = [
  { until: 22000, percent: 35 },
  { until: 27000, percent: 38 },
  { until: 40000, percent: 40 },
  { until: null,  percent: 42 },
];
function _defaultTiersForUnit(unitId) {
  if (unitId === 'unit-jardins' || unitId === 'unit-oscar') return _COMM_3.map(t => ({ ...t }));
  return _COMM_4.map(t => ({ ...t }));
}
function _hydrateCommissionTiers(record) {
  if (!record || record.commissionTiers) return record;
  return { ...record, commissionTiers: _defaultTiersForUnit(record.unitId) };
}

export const getUnitConfig = () => {
  const raw = get(KEYS.UNIT_CONFIG);
  if (!Array.isArray(raw)) return raw;
  return raw.map(_hydrateCommissionTiers);
};
export const saveUnitConfig = (data) => set(KEYS.UNIT_CONFIG, data);

export function upsertPerformance(record) {
  const all = getPerformance();
  const idx = all.findIndex(r => r.id === record.id);
  if (idx >= 0) all[idx] = { ...all[idx], ...record, updatedAt: new Date().toISOString() };
  else all.push({ ...record, updatedAt: new Date().toISOString() });
  savePerformance(all);
  return record;
}

export function upsertUnitPerformance(record) {
  const all = getUnitPerformance();
  const idx = all.findIndex(r => r.id === record.id);
  if (idx >= 0) all[idx] = { ...all[idx], ...record, updatedAt: new Date().toISOString() };
  else all.push({ ...record, updatedAt: new Date().toISOString() });
  saveUnitPerformance(all);
  return record;
}

export function upsertUnitMeta(record) {
  const all = getUnitMetas();
  const idx = all.findIndex(r => r.id === record.id);
  if (idx >= 0) all[idx] = { ...all[idx], ...record, updatedAt: new Date().toISOString() };
  else all.push({ ...record, updatedAt: new Date().toISOString() });
  saveUnitMetas(all);
  return record;
}

export function upsertDailyRevenue(record) {
  const all = getDailyRevenue();
  const idx = all.findIndex(r => r.id === record.id);
  if (idx >= 0) all[idx] = { ...all[idx], ...record, updatedAt: new Date().toISOString() };
  else all.push({ ...record, updatedAt: new Date().toISOString() });
  saveDailyRevenue(all);
  return record;
}

export function upsertUnitConfig(record) {
  const all = getUnitConfig();
  const idx = all.findIndex(r => r.id === record.id);
  if (idx >= 0) all[idx] = { ...all[idx], ...record, updatedAt: new Date().toISOString() };
  else all.push({ ...record, updatedAt: new Date().toISOString() });
  saveUnitConfig(all);
  return record;
}

export function getPerformanceByProfessional(professionalId) {
  return getPerformance().filter(r => r.professionalId === professionalId);
}

export function getPerformanceByUnit(unitId) {
  return getPerformance().filter(r => r.unitId === unitId);
}

export function getPerformanceByPeriod(year, month) {
  return getPerformance().filter(r => r.year === year && r.month === month);
}

export function authenticateUser(email, password) {
  const users = getUsers();
  return users.find(u => u.email === email && u.password === password && u.active) || null;
}

export function changePassword(userId, newPassword) {
  const users = getUsers();
  const idx = users.findIndex(u => u.id === userId);
  if (idx === -1) return false;
  users[idx].password = newPassword;
  users[idx].mustChangePassword = false;
  set(KEYS.USERS, users);
  return true;
}

// resetPasswordToDefault SAIU (16/10/2026): gravava a senha padrão em texto
// só no navegador e a senha real não mudava. O botão "Resetar senha" chama
// /api/auth-admin-set-password { provisoria: true } (senha gerada no servidor).

export function findUserByEmail(email) {
  const users = getUsers();
  return users.find(u => u.email === email && u.active) || null;
}
